import React, { useState, useEffect, useRef } from "react";
import DrawHitbox from "../day4/DrawHitbox";

const Player = ({
	position,
	direction,
	isFalling = false,
	spriteSet,
	anchor = "bottom",
	height = "100px",
	width = "100px",
}) => {
	const [frameIndex, setFrameIndex] = useState(0);
	const lastDirection = useRef("right"); // Stores last movement direction
	const animationInterval = useRef(null);
	const [fallAnimationComplete, setFallAnimationComplete] = useState(false);

	// Determine movement direction
	const getDirectionKey = () => {
		if (direction.dx > 0) return "right";
		if (direction.dx < 0) return "left";
		if (direction.dy > 0) return "down";
		if (direction.dy < 0) return "up";
		return lastDirection.current;
	};

	const directionKey = getDirectionKey();

	// Update last direction **only if movement happens**
	useEffect(() => {
		if (direction.dx !== 0 || direction.dy !== 0) {
			lastDirection.current = directionKey;
		}
	}, [direction]);

	// Handle falling animation
	useEffect(() => {
		if (isFalling) {
			// Only reset fall state if we are newly falling
			if (!fallAnimationComplete) {
				setFrameIndex(0); // Start at first falling frame
				setFallAnimationComplete(true);
				setTimeout(() => {
					setFrameIndex(1); // Move to second fall frame
					// Mark animation as complete
				}, 500);
			}
			return;
		} else {
			// Reset fall animation completion when no longer falling
			setFallAnimationComplete(false);
		}
	}, [isFalling]);

	// Start and stop walking animation
	useEffect(() => {
		if (isFalling) return; // Skip walking animation when falling

		if (direction.dx !== 0 || direction.dy !== 0) {
			if (!animationInterval.current) {
				animationInterval.current = setInterval(() => {
					setFrameIndex((prevIndex) => (prevIndex + 1) % 4);
				}, 100);
			}
		} else {
			setFrameIndex(0);
			clearInterval(animationInterval.current);
			animationInterval.current = null;
		}

		return () => {
			clearInterval(animationInterval.current);
			animationInterval.current = null;
		};
	}, [direction.dx, direction.dy, isFalling]);

	const frameSets = spriteSet.walking;
	const fallingFrames = spriteSet.falling;

	// Choose correct animation
	let playerImage;

	if (isFalling) {
		const frames = ["right", "left"].includes(lastDirection.current)
			? fallingFrames?.[lastDirection.current]
			: fallingFrames?.["right"];
		playerImage = frames?.[frameIndex] ?? frames?.[0] ?? "";
	} else {
		const frames = frameSets?.[directionKey];
		playerImage = frames?.[frameIndex] ?? frames?.[0] ?? "";
	}

	function getAdjustedPosition(position, width, height, anchor = "bottom") {
		const numericWidth = parsePixelValue(width);
		const numericHeight = parsePixelValue(height);

		let adjustedLeft;
		let adjustedTop;

		// Handle horizontal centering
		if (numericWidth !== null) {
			if (anchor === "bottom") {
				adjustedLeft = position.x - numericWidth / 2;
			} else if (anchor === "top-left") {
				adjustedLeft = 0;
			}
		} else {
			adjustedLeft = 0;
		}

		// Handle vertical alignment
		if (numericHeight !== null) {
			if (anchor === "bottom") {
				adjustedTop = position.y - numericHeight;
			} else if (anchor === "top-left") {
				adjustedTop = 0;
			}
		} else {
			adjustedTop = 0;
		}

		return { adjustedLeft, adjustedTop, numericHeight, numericWidth };
	}

	// Helper: parse "100px" => 100, returns null if not in px
	function parsePixelValue(value) {
		if (typeof value === "number") return value;
		if (typeof value === "string" && value.endsWith("px")) {
			return parseFloat(value);
		}
		return null;
	}

	const { adjustedLeft, adjustedTop, numericHeight, numericWidth } =
		getAdjustedPosition(position, width, height, anchor);

	return (
		<>
			<div
				className="absolute flex items-end justify-end z-30 "
				style={{
					left: `${adjustedLeft}px`,
					top: `${adjustedTop}px`,
					height: height,
					width: width,
					transition: "top 0.1s linear, left 0.1s linear",
					pointerEvents: "none",
				}}
			>
				<img height="100%" width="100%" alt="player" src={playerImage} />
			</div>
			<DrawHitbox
				rawX={position.x}
				rawY={position.y}
				x={adjustedLeft}
				y={adjustedTop}
				width={numericWidth}
				height={numericHeight}
				color={"blue"}
			/>
		</>
	);
};

export default Player;
